<script setup>
import { ref } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useServicosStore } from "@/stores/servicos";
import { useMenusCustomizadosStore } from "@/stores/menusCustomizados";
import { useItensMenuStore } from "@/stores/itensMenu";
import { useNotificacoesStore } from "@/stores/notificacoes";
import { useMenuLateralStore } from "@/stores/menuLateral";
import { useBreakpoint } from "@/composables/useBreakpoint";
import { useNotificacaoNavigation } from "@/composables/navigation/useNotificacaoNavigation";
import { useAppNavigation } from "@/composables/navigation/useAppNavigation";
import { storeToRefs } from "pinia";
import Drawer from "primevue/drawer";
import Button from "primevue/button";
import OverlayBadge from "primevue/overlaybadge";
import Skeleton from "primevue/skeleton";
import MensagemResultado from "./MensagemResultado.vue";

// VISIBILIDADE MENU
const menuLateralStore = useMenuLateralStore();

const { isMenuLateralVisible } = storeToRefs(menuLateralStore);

// USER
const authStore = useAuthStore();

const { isAuthenticated } = storeToRefs(authStore);

// ITENS MENU PRINCIPAIS
const itensMenuStore = useItensMenuStore();

const { menusVisiveis } = storeToRefs(itensMenuStore);

// MENUS CUSTOMIZADOS
const menusCustomizadosStore = useMenusCustomizadosStore();

const {
    menusCustomizadosTratados,
    loading: loadingMenusCustomizados
} = storeToRefs(menusCustomizadosStore);

// MENUS SERVIÇOS
const servicosStore = useServicosStore();

const {
    menuInstituicoesCarrossel,
    menuPersonasCarrossel,
    loading: loadingServicos,
    erro
} = storeToRefs(servicosStore);

// NOTIFICAÇÕES
const notificacoesStore = useNotificacoesStore();

const { notificacoesNaoLidasCount } = storeToRefs(notificacoesStore);

// ACCORDION PRIMEVUE
const activeAccordion = ref(null);

// MOBILE/DESKTOP
const { isMobile } = useBreakpoint();

// NAVEGAÇÃO
const { goToHome } = useAppNavigation();

const { goToNotificacoes } = useNotificacaoNavigation();
//

const open = () => menuLateralStore.openMenu();

const close = () => menuLateralStore.closeMenu();

function execCommand(command) {
    menuLateralStore.closeMenu();

    command();
}

defineExpose({ open, close });
</script>

<template>
    <Drawer
        v-model:visible="isMenuLateralVisible"
        :show-close-icon="false"
        :pt:root:style="{ width: isMobile ? '85%' : '400px', border: 'none' }"
        :pt:header:style="{ padding: '25px', background: 'var(--color-primary)', color: 'var(--color-text-light)' }"
        :pt:content:style="{ padding: '0' }"
    >
        <template #header>
            <div class="w-100 d-flex align-items-center justify-content-between gap-2">
                <h2 class="m-0 fs-3 fw-bold">Menu</h2>

                <Button
                    title="Fechar"
                    icon="pi pi-times"
                    size="large"
                    rounded
                    text
                    @click="close()"
                />
            </div>
        </template>

        <div class="mb-5 d-flex flex-column align-items-start">
            <Button
                label="Início"
                icon="fa-solid fa-house"
                severity="secondary"
                size="large"
                class="w-100 mt-2"
                text
                pt:root:class="button-root"
                pt:label:class="button-label"
                pt:icon:class="button-icon"
                @click="execCommand(() => goToHome())"
            />

            <Button
                v-for="itemMenu in menusVisiveis"
                :label="itemMenu.title"
                :icon="itemMenu.icon"
                severity="secondary"
                size="large"
                class="w-100"
                text
                pt:root:class="button-root"
                pt:label:class="button-label"
                pt:icon:class="button-icon"
                @click="execCommand(itemMenu.command)"
            />

            <Button
                v-for="itemMenu in menusCustomizadosTratados"
                :label="itemMenu.title"
                :icon="itemMenu.icon"
                severity="secondary"
                size="large"
                class="w-100"
                text
                pt:root:class="button-root"
                pt:label:class="button-label"
                pt:icon:class="button-icon"
                @click="execCommand(itemMenu.command)"
            />

            <div v-if="loadingMenusCustomizados" class="w-100 px-2">
                <Skeleton width="100%" height="40px"/>
            </div>

            <span class="text-secondary mt-3 mb-2 mx-3">
                Serviços por Instituição
            </span>

            <Button
                v-for="instituicao in menuInstituicoesCarrossel"
                :label="instituicao.title"
                :icon="instituicao.icon"
                severity="secondary"
                size="large"
                class="w-100"
                text
                pt:root:class="button-root"
                pt:label:class="button-label fs-6"
                pt:icon:class="button-icon"
                @click="execCommand(instituicao.command)"
            />

            <div v-if="loadingServicos" class="w-100 px-2 d-flex flex-column gap-2">
                <Skeleton width="100%" height="40px"/>

                <Skeleton width="100%" height="40px"/>
            </div>

            <span class="text-secondary mt-3 mb-2 mx-3">
                Serviços por Persona
            </span>

            <Button
                v-for="persona in menuPersonasCarrossel"
                :label="persona.title"
                :icon="persona.icon"
                severity="secondary"
                size="large"
                class="w-100"
                text
                pt:root:class="button-root"
                pt:label:class="button-label fs-6"
                pt:icon:class="button-icon"
                @click="execCommand(persona.command)"
            />

            <div v-if="loadingServicos" class="w-100 px-2 d-flex flex-column gap-2">
                <Skeleton width="100%" height="40px"/>

                <Skeleton width="100%" height="40px"/>
            </div>

            <MensagemResultado
                :visible="erro && !loadingServicos"
                tipo="erro"
                titulo="Não foi possível carregar Instituições"
                size="small"
            />
        </div>
    </Drawer>
</template>

<style scoped>

:deep(.button-root) {
    justify-content: start;
    border-radius: 0;
}

:deep(.button-label) {
    color: var(--color-primary);
    font-weight: 400;
    text-align: start;
}

:deep(.button-icon) {
    margin-bottom: 3px;
    color: var(--color-primary);
}

:deep(.p-panelmenu-submenu) {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

:deep(.p-panelmenu-item-link) {
    justify-content: space-between;
    flex-direction: row-reverse;
}

:deep(.p-overlaybadge .p-badge) {
    transform: translate(30%, -45%);
    outline: none;
}
</style>
